import Image from 'next/image';
import RecipeGraph from 'compositions/RecipeGraph';
import RichText from 'components/ui/RichText';
import Text from 'components/ui/Text';
import { FC, useEffect, useState } from 'react';
import { imageDimensions, imageUrl } from 'api';
import { RecipeProps, RecipeCategoryTexts } from 'type/recipe';
import { RecipeValueFactory } from 'factories/recipeValueFactory';
import { selectRoomState, selectSelectedRoomOptions } from 'store/roomSlice';
import { useAppSelector } from 'store/hooks';
import {
  MainValueCategory,
  NormalizedRecipeValues,
  NormalizedValue,
} from 'type';
import { useTranslation } from 'hooks/useTranslation';
import CtaBlock from 'compositions/CtaBlock';

type ColorBoxProps = {
  number: NormalizedValue;
  value: NormalizedValue | number;
  colorType: MainValueCategory;
};

const getColorClass = (
  color: NormalizedValue | number,
  colorType: 'financial' | 'human' | 'sustainability',
) => {
  const colors = {
    financial: [
      'bg-financial-1',
      'bg-financial-2',
      'bg-financial-3',
      'bg-financial-4',
      'bg-financial-5',
    ],
    human: [
      'bg-human-1',
      'bg-human-2',
      'bg-human-3',
      'bg-human-4',
      'bg-human-5',
    ],
    sustainability: [
      'bg-sustainability-1',
      'bg-sustainability-2',
      'bg-sustainability-3',
      'bg-sustainability-4',
      'bg-sustainability-5',
    ],
  };

  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  return colors[colorType][Math.floor(color) - 1];
};

const ColorBox: FC<ColorBoxProps> = ({ number, value, colorType }) => (
  <div
    className={`p-1 sm:p-2 lg:p-4 flex items-center justify-center ${getColorClass(
      number,
      colorType,
    )}`}
  >
    {number === Math.floor(value) && (
      <div className="rounded-full bg-white aspect-square h-4 w-4 sm:h-6 sm:w-6 md:h-8 md:w-8 drop-shadow-xl" />
    )}
  </div>
);

type RecipeChunkProps = {
  title: string;
  categoryTexts: RecipeCategoryTexts;
  value: NormalizedValue | number;
  colorType: MainValueCategory;
};

const RecipeChunk: FC<RecipeChunkProps> = ({
  title,
  categoryTexts,
  value,
  colorType,
}) => {
  const normalizedValue = Math.max(Math.ceil(value), 1) as NormalizedValue;
  const translation = useTranslation();

  return (
    <>
      <Text
        bold
        color="grey-800"
        elementType="h3"
        size="large"
        className="mb-4"
      >
        {title}
      </Text>

      <div className="grid grid-cols-5">
        <ColorBox number={1} value={normalizedValue} colorType={colorType} />
        <ColorBox number={2} value={normalizedValue} colorType={colorType} />
        <ColorBox number={3} value={normalizedValue} colorType={colorType} />
        <ColorBox number={4} value={normalizedValue} colorType={colorType} />
        <ColorBox number={5} value={normalizedValue} colorType={colorType} />
      </div>
      <div className="flex justify-between mb-8">
        <Text bold color="grey-500" className="small-caps">
          {translation('recipe.values.low')}
        </Text>
        <Text bold color="grey-500" className="small-caps">
          {translation('recipe.values.high')}
        </Text>
      </div>

      <div className="rounded-lg bg-grey-200 p-4 mb-8">
        <RichText text={categoryTexts[`text${normalizedValue}`]} />
      </div>
      <div className="mb-16">
        <RichText text={categoryTexts.general} />
      </div>
    </>
  );
};

type SelectedOptionProps = {
  title?: string;
  label: string;
};

const SelectedOption: FC<SelectedOptionProps> = ({ title, label }) => (
  <div>
    <Text bold color="grey-500" elementType="div" className="small-caps">
      {label}
    </Text>
    <Text bold color="grey-800" elementType="div" size="large">
      {title}
    </Text>
  </div>
);

const Recipe = () => {
  const currentRoom = useAppSelector(selectRoomState);
  const translation = useTranslation();
  const [recipe, setRecipe] = useState<RecipeProps | null>(null);
  const currentRoomOptions = useAppSelector(selectSelectedRoomOptions);
  const [normalizedRecipeValues, setNormalizedRecipeValues] =
    useState<NormalizedRecipeValues | null>(null);

  useEffect(() => {
    if (currentRoom) {
      setRecipe(currentRoom.recipe);
    }

    const combinedValues = RecipeValueFactory.combineValues(currentRoomOptions);
    const normalizedValues = RecipeValueFactory.normalizeValues(
      currentRoom,
      combinedValues,
    );
    setNormalizedRecipeValues(normalizedValues);
  }, [currentRoomOptions, currentRoom]);

  const roomSlug = currentRoom?.slug?.current.toLowerCase() as
    | 'open-plan-office'
    | 'individual-office'
    | 'conference-room'
    | 'classroom';

  return (
    recipe &&
    normalizedRecipeValues && (
      <div className="text-content">
        {currentRoomOptions && (
          <div
            className="relative bg-grey-50 -mx-8 lg:mx-0"
            style={{
              aspectRatio: imageDimensions(
                currentRoomOptions.lightingSolution?.image,
              ).aspectRatio,
            }}
          >
            <Image
              alt={recipe.title}
              src={imageUrl(
                currentRoomOptions.lightingSolution?.image,
              ).toString()}
              fill
              className="object-cover"
            />
          </div>
        )}
        <Text
          bold
          size="huge"
          color="grey-800"
          elementType="h1"
          className="mb-8 mt-10"
        >
          {recipe.title}
        </Text>
        <Text elementType="div" size="preamble" className="mb-16">
          <RichText text={recipe.preamble} />
        </Text>
        {currentRoomOptions && (
          <>
            <div className="ipadportait:flex ipadportait:space-x-10 space-y-8 ipadportait:space-y-0">
              <SelectedOption
                label={translation('room.optionsCategory.preset')}
                title={currentRoomOptions.lightingSolution?.title}
              />

              <SelectedOption
                label={translation('room.optionsCategory.lightingControl')}
                title={translation(
                  `lightingSolutions.controlType.${currentRoomOptions.controlType}`,
                )}
              />

              {currentRoomOptions.adjustments &&
                currentRoomOptions.adjustments['presence-level'] && (
                  <>
                    <SelectedOption
                      label={translation('room.optionsCategory.presenceLevel')}
                      title={
                        currentRoomOptions.adjustments['presence-level'].title
                      }
                    />
                  </>
                )}
            </div>
          </>
        )}
        <div className="grid gap-8 my-16 md:my-32 max-w-[800px]">
          <RecipeGraph large />
        </div>
        {/* SUSTAINABILITY */}
        <Text
          bold
          color="grey-800"
          elementType="h2"
          size="xlarge"
          className="md:mb-4"
        >
          {translation('recipe.categories.sustainability')}
        </Text>
        <RecipeChunk
          title={translation('recipe.values.co2')}
          categoryTexts={recipe.texts.co2}
          value={normalizedRecipeValues.co2}
          colorType="sustainability"
        />
        {/* HUMAN */}
        <Text
          bold
          color="grey-800"
          elementType="h2"
          size="xlarge"
          className="md:mb-4"
        >
          {translation('recipe.categories.human')}
        </Text>
        <RecipeChunk
          title={translation('recipe.values.visual')}
          categoryTexts={recipe.texts.visual}
          value={normalizedRecipeValues.visual}
          colorType="human"
        />
        <RecipeChunk
          title={translation('recipe.values.biological')}
          categoryTexts={recipe.texts.biological}
          value={normalizedRecipeValues.biological}
          colorType="human"
        />
        <RecipeChunk
          title={translation('recipe.values.emotional')}
          categoryTexts={recipe.texts.emotional}
          value={normalizedRecipeValues.emotional}
          colorType="human"
        />
        {/* FINANCIAL */}
        <Text
          bold
          color="grey-800"
          elementType="h2"
          size="xlarge"
          className="md:mb-4"
        >
          {translation('recipe.categories.financial')}
        </Text>
        <RecipeChunk
          title={translation('recipe.values.luminaireCost')}
          categoryTexts={recipe.texts.luminaireCost}
          value={normalizedRecipeValues.luminaireCost}
          colorType="financial"
        />
        <RecipeChunk
          title={translation('recipe.values.energyCost')}
          categoryTexts={recipe.texts.energyCost}
          value={normalizedRecipeValues.energyCost}
          colorType="financial"
        />
        <CtaBlock
          title={translation('cta.title')}
          text={translation(`cta.text.${roomSlug}`)}
        />
      </div>
    )
  );
};

export default Recipe;
